import MUIDataTable from 'mui-datatables';
import { Box, Grid, Chip, IconButton, Tab, Button, Typography, Tooltip, useMediaQuery } from '@mui/material';
import { variants } from '../../../../variants/variants';
import { useChangeStatusMutation, useGetJuniorFormsQuery } from '../../giftApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { selectJuniorPendingForms, setPreview } from '../../giftSlice';
import { CheckOutlined, ManageHistoryOutlined, PreviewOutlined } from '@mui/icons-material';
import { boxBackground, colors } from '../../utility/status';
import ApproveDialog from '../common/ApproveDialog';
import { useEffect, useState } from 'react';
import { TabContext, TabList } from '@mui/lab';
import SkeletonTable from '../../../RnR/AdminRnR/SkeletonTable';
import { toggleSwipe } from '../../../Search/searchSlice';
import CustomRow from './CustomRow';
import { useTheme } from '@emotion/react';
import CustomEmptyTable from '../common/CustomEmptyTable';

const GiftTable = () => {
  const { isLoading } = useGetJuniorFormsQuery();
  const [approveApplication] = useChangeStatusMutation();
  const dispatch = useDispatch();
  const [selected, setSelected] = useState('');
  const forms = useSelector(selectJuniorPendingForms);
  const [openDialog, setOpenDialog] = useState(false);
  const [openChangeDialog, setOpenChangeDialog] = useState(false);
  const [result, setResult] = useState('Pending');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const theme = useTheme();
  let noData = Boolean(!forms[result] || forms[result]?.length === 0);

  const handleViewForm = (form) => {
    dispatch(
      setPreview({
        ...form,
        my: false,
      })
    );
  };
  const handleChange = (event, newValue) => {
    setResult(newValue);
  };
  const handleCloseDialog = (e) => {
    setOpenDialog(false);
  };
  const handleOpenDialog = (id) => {
    setSelected(id);
    setOpenDialog(true);
  };
  const handleCloseChangeDialog = (e) => {
    setOpenChangeDialog(false);
  };
  const handleOpenChangeDialog = (id) => {
    setSelected(id);
    setOpenChangeDialog(true);
  };
  const handleRowClick = (rowData, { dataIndex }) => {
    dispatch(toggleSwipe({ value: true }));
    window['ReactNativeWebView'] &&
      window['ReactNativeWebView']?.postMessage(JSON.stringify({ type: 'swipe', value: true }));
    handleViewForm({
      response: forms[result][dataIndex].response,
      currentDesignation: forms[result][dataIndex].currentDesignation,
      desiredDesignation: forms[result][dataIndex].desiredDesignation,
      edit: true,
    });
  };

  if (isLoading)
    return (
      <Grid xs={12} md={8}>
        <SkeletonTable />
      </Grid>
    );

  const columns = [
    { name: '_id', options: { display: false } },
    {
      name: 'employeeDetails',
      label: isMobile ? 'Application' : 'Employee',
      options: {
        sort: false,
        display: noData ? false : true,
        customBodyRender: (val, tableMeta) => {
          const row = tableMeta.rowData;
          return isMobile ? (
            <CustomRow
              gift={row}
              handleOpenChangeDialog={handleOpenChangeDialog}
              handleOpenDialog={handleOpenDialog}
              result={result}
            />
          ) : (
            <Typography sx={{ fontSize: variants.BODY }}>{val?.empName}</Typography>
          );
        },
      },
    },
    {
      name: 'currentDesignation',
      label: 'From',
      options: {
        sort: false,
        display: !isMobile,
        customBodyRender: (value) => {
          return <Typography sx={{ fontSize: variants.BODY }}>{value.split(' - ')[0]}</Typography>;
        },
      },
    },
    {
      name: 'desiredDesignation',
      label: 'To',
      options: {
        sort: false,
        display: !isMobile,

        customBodyRender: (value) => {
          return <Typography sx={{ fontSize: variants.BODY }}>{value.split(' - ')[0]}</Typography>;
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        sort: false,
        display: !isMobile,

        customBodyRender: (value) => {
          let status = value.split('_').join(' ');
          return (
            <Chip
              variant='outlined'
              sx={{ background: boxBackground[status], border: 0, color: colors[status], fontWeight: 700 }}
              label={status}
            />
          );
        },
      },
    },
    {
      name: 'submissionDate',
      label: 'Submitted on',
      options: {
        sort: false,
        display: !isMobile,

        customBodyRender: (value) => {
          return <Typography sx={{ fontSize: variants.BODY }}>{new Date(value).toLocaleDateString()}</Typography>;
        },
      },
    },
    {
      name: 'result',
      label: 'Actions',
      options: {
        sort: false,
        display: isMobile ? false : result === 'Pending' ? (noData ? false : true) : false,
        customBodyRender: (_, tableMeta) => {
          const id = tableMeta.rowData[0];
          return (
            <>
              <Tooltip title={'Requires changes'}>
                <IconButton
                  variant='outlined'
                  sx={{ borderColor: colors.Pending, color: colors.Pending }}
                  onClick={() => handleOpenChangeDialog(id)}
                >
                  <ManageHistoryOutlined fontSize='small' sx={{ color: colors.Pending }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={'Approve'}>
                <IconButton
                  variant='outlined'
                  sx={{ borderColor: colors.Pass, color: colors.Pass, ml: 1 }}
                  onClick={() => handleOpenDialog(id)}
                >
                  <CheckOutlined fontSize='small' sx={{ color: colors.Pass }} />
                </IconButton>
              </Tooltip>
            </>
          );
        },
      },
    },
    {
      name: 'response',
      label: 'Application',
      options: {
        filter: false,
        sort: false,
        display: !isMobile,

        customBodyRender: (value, tableMeta) => {
          return (
            <IconButton
              size='small'
              onClick={() =>
                handleViewForm({
                  response: value,
                  currentDesignation: tableMeta.rowData[2],
                  desiredDesignation: tableMeta.rowData[3],
                  edit: result === 'Pending' ? true : false,
                })
              }
            >
              <PreviewOutlined />
            </IconButton>
          );
        },
      },
    },
  ];
  return (
    <Grid
      item
      xs={12}
      md={8}
      sx={{
        background: isMobile ? '' : variants.boxBackground.PROFILE_CARD,
        borderRadius: variants.boxRadius.MAIN,
        p: 1,
        pb: 0,
        [theme.breakpoints.down('lg')]: {
          px: 0,
          '& .MuiTableCell-footer': {
            p: 0,
            border: 0,
          },
        },
      }}
    >
      <Box
        sx={{
          overflow: 'hidden',
          background: '#fff',
          border: 1,
          borderColor: 'divider',
          borderRadius: variants.boxRadius.MAIN,
          height:isMobile ? "85%" : "100%"
        }}
      >
        <TabContext value={result}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: isMobile ? 0 : 2 }}>
            <TabList onChange={handleChange}>
              {Object.keys(forms)?.map((res, index) => {
                return (
                  <Tab
                    sx={{ fontSize: variants.TINY, color: colors[res] }}
                    key={index}
                    label={
                      <span>
                        {res}
                        <span
                          style={{
                            marginLeft: '0.5rem',
                            paddingBlock: '0.15rem',
                            paddingInline: '0.75rem',
                            background: boxBackground[res],
                            borderRadius: 5,
                            color: colors[res],
                          }}
                        >
                          {(forms && forms[res]?.length) || 0}
                        </span>
                      </span>
                    }
                    value={res}
                  />
                );
              })}
            </TabList>
          </Box>
        </TabContext>
        <MUIDataTable
          title={''}
          data={forms[result]}
          columns={columns}
          options={{
            elevation: 0,
            selectableRows: 'none',
            rowsPerPage: 5,
            rowsPerPageOptions: [5, 10, 20],
            tableBodyHeight: `calc(100vh - ${isMobile ? '245px' : '265px'})`,
            viewColumns: false,
            search: false,
            download: false,
            filter: false,
            print: false,
            responsive: 'standard',
            onRowClick: isMobile ? handleRowClick : () => {},
          }}
          components={{
            ...(noData && {
              TableBody: CustomEmptyTable,
            }),
          }}
        />
        <ApproveDialog
          status={'Manager_approved'}
          open={openDialog}
          handleClose={handleCloseDialog}
          title={'Approve GIFT application'}
          label={'Please give inputs with respect to this GIFT application:'}
          textarea={true}
          selected={selected}
        />
        <ApproveDialog
          status={'Requires_changes'}
          open={openChangeDialog}
          handleClose={handleCloseChangeDialog}
          title={'Requires changes in GIFT application'}
          label={'Please write the changes you want to highlight:'}
          textarea={true}
          selected={selected}
        />
      </Box>
    </Grid>
  );
};

export default GiftTable;
